import React from 'react'
import { ControlledModal } from '../../layouts/components/modal'
import { School, Teacher, useAppContext } from '../../providers/context'
import { makeRequest, requestOrUndefined, showAxiosErrorAsToast } from '../../request'
import { Button, ButtonShape, ButtonStyle } from '../../components/simple/button'
import { mapWithDividerSimple, prepareDeltaToPost } from '../../utils'
import { Checkbox } from '../../components/form/checkbox'
import { IconEdit } from '../../icons/library'
import { Popups } from '../../providers/popups'
import { getNormalTagsForUser, QuillTextarea } from '../../components/quill/quillTextarea'
import Delta from 'quill-delta'
import { Toasts } from '../../providers/toast'

export const AttendanceSettingsModal: React.FC<{ user: Teacher; onClose: () => void }> = ({ user, onClose }) => {
  const { reloadUser } = useAppContext()
  const [loading, setLoading] = React.useState(false)
  const schoolsToShow = user.districtAccess ? user.district.schools : user.schools
  const [attendanceEnabled, setAttendanceEnabled] = React.useState<Record<number, boolean>>(
    schoolsToShow.reduce((acc, school) => ({ ...acc, [school.id]: school.attendanceEnabled }), {})
  )
  const [attendanceReplies, setAttendanceReplies] = React.useState<Record<number, boolean>>(
    schoolsToShow.reduce((acc, school) => ({ ...acc, [school.id]: school.attendanceReplies }), {})
  )
  const [attendanceMessages, setAttendanceMessages] = React.useState<Record<number, string | undefined>>(
    schoolsToShow.reduce((acc, school) => ({ ...acc, [school.id]: school.attendanceMessage }), {})
  )
  const [attendanceMessageDeltas, setAttendanceMessageDeltas] = React.useState<Record<number, Delta | undefined>>(
    schoolsToShow.reduce((acc, school) => ({ ...acc, [school.id]: undefined }), {})
  )
  const [openAttendanceMessagesSchoolID, setOpenAttendanceMessagesSchoolID] = React.useState<number | undefined>(
    undefined
  )
  const [attendanceMessagesPerClass, setAttendanceMessagesPerClass] = React.useState<Record<number, boolean>>(
    schoolsToShow.reduce(
      (acc, school) => ({ ...acc, [school.id]: !!school.attendanceMessage?.includes('[attendance.class]') }),
      {}
    )
  )

  const tags = React.useMemo(() => getNormalTagsForUser(user), [user])

  return (
    <ControlledModal
      size="md"
      onOpenChange={(o) => {
        if (!o) onClose()
      }}
      open={true}
      title={`Edit ${user.districtAccess ? 'District' : 'School'} Attendance Settings`}
      disabled={loading}
    >
      {mapWithDividerSimple(schoolsToShow, (school) => (
        <React.Fragment key={school.id}>
          <div className="flex items-center justify-between gap-2">
            <div>{school.name}</div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={attendanceEnabled[school.id]}
                onChecked={async (v) => {
                  if (!v) {
                    setAttendanceEnabled((a) => ({ ...a, [school.id]: false }))
                  } else {
                    Popups.confirm({
                      title: <div className="text-lg font-bold">Caution</div>,
                      message: (
                        <>
                          By turning this on, your families will get attendance messages when students miss class. The
                          tech team will need to set this connection up for your school.{' '}
                          <b>Do not turn on if you are unsure</b>, check with your tech team or help@reachmyteach.com
                        </>
                      ),
                      yesText: 'Continue',
                      noText: 'Cancel',
                    }).then((confirmed) => {
                      if (confirmed) setAttendanceEnabled((a) => ({ ...a, [school.id]: true }))
                    })
                  }
                }}
              />
              Enable Attendance
              {attendanceEnabled[school.id] && (
                <>
                  <Checkbox
                    className="ml-3"
                    checked={attendanceReplies[school.id]}
                    onChecked={(v) => {
                      setAttendanceReplies((a) => ({ ...a, [school.id]: v }))
                      if (attendanceMessages[school.id] !== undefined)
                        Toasts.showToast({
                          message:
                            'Please confirm that your attendance message is up to date with the new reply setting.',
                        })
                    }}
                  />
                  Allow Attendance Replies
                  <IconEdit
                    className="cursor-pointer"
                    onClick={() => setOpenAttendanceMessagesSchoolID((s) => (s === school.id ? undefined : school.id))}
                  />
                </>
              )}
            </div>
          </div>
          {openAttendanceMessagesSchoolID === school.id && (
            <>
              <div className="mt-2 flex items-center justify-between gap-2">
                <b>Set the message that will be sent to parents when their child is marked absent.</b>
                {attendanceMessages[school.id] && (
                  <Button
                    shape={ButtonShape.small}
                    buttonStyle={ButtonStyle.light}
                    onClick={() => {
                      setAttendanceMessages((a) => ({ ...a, [school.id]: undefined }))
                    }}
                  >
                    Reset Message
                  </Button>
                )}
              </div>
              {!attendanceMessages[school.id] ? (
                <div className="flex items-center justify-between gap-2">
                  Is {school.name}'s attendance reported per class or per day?
                  <div className="flex gap-2">
                    <Button
                      onClick={async () => {
                        setLoading(true)
                        const message = await getDefaultAttendanceMessage(attendanceReplies[school.id], true, school)
                        setLoading(false)
                        setAttendanceMessages((a) => ({ ...a, [school.id]: message }))
                        setAttendanceMessagesPerClass((a) => ({ ...a, [school.id]: true }))
                      }}
                    >
                      Per Class
                    </Button>
                    <Button
                      onClick={async () => {
                        setLoading(true)
                        const message = await getDefaultAttendanceMessage(attendanceReplies[school.id], false, school)
                        setLoading(false)
                        setAttendanceMessages((a) => ({ ...a, [school.id]: message }))
                        setAttendanceMessagesPerClass((a) => ({ ...a, [school.id]: false }))
                      }}
                    >
                      Per Day
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {attendanceMessagesPerClass[school.id] && (
                    <div className="text-sm">
                      You can use <span className="message-script">[attendance.class]</span> to insert the missed class
                      name into the message.
                    </div>
                  )}
                  <QuillTextarea
                    className="rounded"
                    valueHTML={attendanceMessages[school.id]}
                    onValueChange={(v, d) => {
                      setAttendanceMessages((a) => ({ ...a, [school.id]: v }))
                      setAttendanceMessageDeltas((a) => ({ ...a, [school.id]: d }))
                    }}
                    tags={tags}
                    flagPoorTranslations
                    below={
                      attendanceReplies[school.id] && (
                        <div className="px-2 py-1 text-sm text-gray-900">
                          "To view and excuse this absence, click <span className="text-link underline">here</span>"
                        </div>
                      )
                    }
                  />
                  {attendanceReplies[school.id] && (
                    <div className="mb-4 text-sm">
                      The last line of the message is not editable and depends on the contact method through which the
                      notification was sent.
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </React.Fragment>
      ))}

      <Button
        logo
        disabled={loading}
        className="mt-3 self-start"
        onClick={() => {
          setLoading(true)
          makeRequest<void>('/frontend_api/updateAttendanceSettings', {
            schools: schoolsToShow.map((school) => ({
              id: school.id,
              attendanceEnabled: attendanceEnabled[school.id],
              attendanceReplies: attendanceReplies[school.id],
              attendanceMessage: attendanceMessageDeltas[school.id]
                ? prepareDeltaToPost(attendanceMessageDeltas[school.id])
                : undefined,
            })),
          })
            .then(() => {
              reloadUser()
              onClose()
            })
            .catch(showAxiosErrorAsToast)
            .finally(() => setLoading(false))
        }}
      >
        Save
      </Button>
    </ControlledModal>
  )
}

const getDefaultAttendanceMessage = async (allowReplies: boolean, perClass: boolean, school: School) => {
  return (
    requestOrUndefined<string>('/frontend_api/getDefaultAttendanceMessage', {
      perClass,
      name: school.name,
      phoneNumber: school.displayPhoneNumber,
      allowReplies,
    }) || ''
  )
}
